import { useEffect, useState } from "react";
import { useConexion } from "../lib/conexion";
import {
  enviarCola,
  quitarDeLaCola,
  reintentar,
  useSinConexion,
} from "../lib/sinConexion";
import { creditos, fechaHora, formatearCantidad } from "../lib/formato";
import { Aviso, Modal } from "./piezas";
import { useIdioma } from "../lib/idioma";

/**
 * La cinta de la conexión y la cola de entregas pendientes.
 *
 * Está en todas las pantallas, no solo en el mostrador: quien atiende tiene
 * que saber que la red se ha ido aunque esté mirando otra cosa, y las
 * entregas en cola se envían desde aquí, para que salgan aunque nadie vuelva
 * al mostrador. Se prueba a enviar al volver la red y cada medio minuto
 * mientras quede algo.
 *
 * Una entrega que el servidor rechaza de plano —no por falta de red— se queda
 * en la cola con su motivo y aquí se decide: reintentar o descartar. No se
 * descarta sola una venta.
 */
export default function EstadoConexion() {
  const { t, cuenta } = useIdioma();
  const { enLinea, desdeCuando } = useConexion();
  const { cola, paquete } = useSinConexion();
  const [mirando, setMirando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const pendientes = cola.filter((e) => !e.error);
  const rechazadas = cola.filter((e) => e.error);

  async function enviarAhora() {
    setEnviando(true);
    try {
      await enviarCola();
    } finally {
      setEnviando(false);
    }
  }

  useEffect(() => {
    if (!enLinea || pendientes.length === 0) return;
    void enviarAhora();
    const cada = setInterval(() => void enviarAhora(), 30_000);
    return () => clearInterval(cada);
    // Se dispara al volver la red y cuando entra algo nuevo en la cola.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enLinea, pendientes.length]);

  if (enLinea && cola.length === 0) return null;

  return (
    <>
      <div
        className={`cinta-conexion ${enLinea ? "pendiente" : "caida"}`}
        role="status"
      >
        <div>
          {!enLinea ? (
            <>
              <strong>
                {desdeCuando
                  ? t("ec.sinConexionDesde", {
                      hora: new Date(desdeCuando).toLocaleTimeString("es-ES", {
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                    })
                  : t("ec.sinConexion")}
              </strong>{" "}
              {paquete ? t("ec.conPaquete") : t("ec.sinPaquete")}
            </>
          ) : (
            <>
              <strong>{cuenta(pendientes.length, "ec.unaPendiente", "ec.pendientes")}</strong>
              {rechazadas.length > 0 && (
                <> · {cuenta(rechazadas.length, "ec.unaRechazada", "ec.rechazadas")}</>
              )}
            </>
          )}
          {enLinea && pendientes.length === 0 && rechazadas.length > 0 && (
            <> {cuenta(rechazadas.length, "ec.hayRechazada", "ec.hayRechazadas")}</>
          )}
        </div>
        <div className="fila" style={{ gap: 6 }}>
          {cola.length > 0 && (
            <button
              className="boton secundario"
              onClick={() => setMirando(true)}
            >
              {t("ec.verCola", { n: cola.length })}
            </button>
          )}
          {enLinea && pendientes.length > 0 && (
            <button
              className="boton"
              disabled={enviando}
              onClick={() => void enviarAhora()}
            >
              {enviando ? t("ec.enviando") : t("ec.enviarAhora")}
            </button>
          )}
        </div>
      </div>

      {mirando && (
        <Modal
          titulo={t("ec.titulo")}
          alCerrar={() => setMirando(false)}
        >
          <p className="suave" style={{ marginTop: 0 }}>
            {t("ec.explicacion")}
          </p>
          {cola.length === 0 ? (
            <Aviso tipo="ok">{t("ec.nadaPorEnviar")}</Aviso>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("ec.cuando")}</th>
                    <th>{t("ec.socio")}</th>
                    <th>{t("ec.que")}</th>
                    <th className="num">{t("ec.importe")}</th>
                    <th>{t("ec.estado")}</th>
                  </tr>
                </thead>
                <tbody>
                  {cola.map((e) => (
                    <tr key={e.clave}>
                      <td
                        className="pequeno suave"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {fechaHora(e.hechaEn)}
                      </td>
                      <td>
                        {e.socioNombre}
                        <div className="suave pequeno">#{e.numeroSocio}</div>
                      </td>
                      <td className="pequeno">
                        {e.lineas
                          .map(
                            (l) =>
                              `${l.nombre} · ${formatearCantidad(Math.round(l.cantidadG * 1000), l.unidad)}`,
                          )
                          .join(", ")}
                      </td>
                      <td className="num tabular">
                        {creditos(e.totalCentimosPrevisto)}
                      </td>
                      <td className="pequeno">
                        {e.error ? (
                          <>
                            <span style={{ color: "var(--rojo)" }}>
                              {t("ec.rechazada", { motivo: e.error })}
                            </span>
                            <div
                              className="fila"
                              style={{ gap: 4, marginTop: 6 }}
                            >
                              <button
                                className="boton secundario"
                                onClick={() => reintentar(e.clave)}
                              >
                                {t("ec.reintentar")}
                              </button>
                              <button
                                className="boton fantasma"
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      t("ec.confirmarDescartar"),
                                    )
                                  )
                                    quitarDeLaCola(e.clave);
                                }}
                              >
                                {t("ec.descartar")}
                              </button>
                            </div>
                          </>
                        ) : (
                          <span className="suave">
                            {t("ec.pendienteDeEnviar")}
                            {e.intentos > 0 ? cuenta(e.intentos, "ec.unIntento", "ec.intentos") : ""}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
